import React, { useEffect, useState } from 'react';
import { BookOpen, ChevronDown, ChevronUp, Clock, ExternalLink, Loader2, Trash2, Users } from 'lucide-react';
import { BOOK_RECIPES } from '../data/bookRecipes';

const API_URL = import.meta.env.VITE_API_URL;

const SavedRecipes = () => {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState('book-mme-mediterranean-power-bowl');
  const [deleting, setDeleting] = useState(null);

  const token = localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchRecipes = async () => {
    try {
      const res = await fetch(`${API_URL}/recipes`, { headers });
      if (res.ok) {
        const data = await res.json();
        setRecipes(data);
      }
    } catch (err) {
      console.error('Failed to load recipes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecipes();
  }, []);

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await fetch(`${API_URL}/recipes/${id}`, { method: 'DELETE', headers });
      setRecipes((prev) => prev.filter((r) => r.id !== id));
      if (expanded === id) setExpanded(null);
    } catch (err) {
      console.error('Failed to delete recipe:', err);
    } finally {
      setDeleting(null);
    }
  };

  const parseJSON = (val, fallback = []) => {
    if (!val) return fallback;
    if (Array.isArray(val)) return val;
    try { return JSON.parse(val); } catch { return fallback; }
  };

  const macroColor = (label) => {
    if (label === 'Calories') return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300';
    if (label === 'Protein') return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
    if (label === 'Carbs') return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
    if (label === 'Fat') return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
    return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  };

  const RecipeCard = ({ recipe, canDelete = false }) => {
    const ingredients = parseJSON(recipe.ingredients);
    const instructions = parseJSON(recipe.instructions);
    const tags = parseJSON(recipe.tags);
    const isOpen = expanded === recipe.id;

    return (
      <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{recipe.name}</h2>
                {recipe.category && (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 text-xs font-bold">
                    {recipe.category}
                  </span>
                )}
              </div>

              {recipe.description && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{recipe.description}</p>
              )}

              <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                {recipe.prep_time && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" /> Prep: {recipe.prep_time}
                  </span>
                )}
                {recipe.cook_time && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" /> Cook: {recipe.cook_time}
                  </span>
                )}
                {recipe.servings && (
                  <span className="flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" /> {recipe.servings} serving{Number(recipe.servings) === 1 ? '' : 's'}
                  </span>
                )}
              </div>

              {(recipe.calories || recipe.protein) && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {[
                    { label: 'Calories', val: recipe.calories, unit: 'kcal' },
                    { label: 'Protein', val: recipe.protein, unit: 'g' },
                    { label: 'Carbs', val: recipe.carbs, unit: 'g' },
                    { label: 'Fat', val: recipe.fat, unit: 'g' },
                  ].filter((m) => m.val !== null && m.val !== undefined).map((m) => (
                    <span key={m.label} className={`px-2 py-0.5 rounded-full text-xs font-medium ${macroColor(m.label)}`}>
                      {m.label}: {m.val}{m.unit}
                    </span>
                  ))}
                </div>
              )}

              {tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {tags.slice(0, 5).map((tag) => (
                    <span key={tag} className="px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-full text-xs">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {recipe.source_url && (
                <a
                  href={recipe.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                  title="View original recipe"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
              {canDelete && (
                <button
                  onClick={() => handleDelete(recipe.id)}
                  disabled={deleting === recipe.id}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                  title="Delete recipe"
                >
                  {deleting === recipe.id
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Trash2 className="w-4 h-4" />}
                </button>
              )}
              <button
                onClick={() => setExpanded(isOpen ? null : recipe.id)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  isOpen
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                    : 'bg-gray-100 text-gray-600 hover:bg-blue-50 hover:text-blue-600 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                {isOpen ? 'Hide' : 'View'}
              </button>
            </div>
          </div>
        </div>

        {isOpen && (
          <div className="border-t border-gray-100 dark:border-gray-700 p-5 grid md:grid-cols-2 gap-6">
            {ingredients.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-3 text-sm uppercase tracking-wide">Ingredients</h3>
                <ul className="space-y-1.5">
                  {ingredients.map((ing, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-2 flex-shrink-0" />
                      {ing}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {instructions.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-3 text-sm uppercase tracking-wide">Instructions</h3>
                <ol className="space-y-2">
                  {instructions.map((step, i) => (
                    <li key={i} className="flex gap-3 text-sm text-gray-700 dark:text-gray-300">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold">{i + 1}</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}
      </article>
    );
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <BookOpen className="w-7 h-7 text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Recipes</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            25 Macros Made Easy book recipes plus recipes saved by your AI Nutrition Coach
          </p>
        </div>
      </div>

      <section className="mb-10">
        <div className="mb-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Macros Made Easy Book Recipes</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Included with Integrative Health Journal app access.</p>
          </div>
          <span className="text-xs font-bold text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-300 px-3 py-1 rounded-full">
            {BOOK_RECIPES.length} included recipes
          </span>
        </div>
        <div className="space-y-4">
          {BOOK_RECIPES.map((recipe) => (
            <RecipeCard key={recipe.id} recipe={recipe} />
          ))}
        </div>
      </section>

      <section>
        <div className="mb-4">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">AI-Saved Recipes</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Recipes your AI Nutrition Coach finds and saves for you.</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : recipes.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-8 text-center">
            <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-medium text-gray-700 dark:text-gray-300 mb-2">No AI-saved recipes yet</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Ask your AI Nutrition Coach to find a recipe and it will be saved here automatically.
            </p>
            <p className="text-xs text-blue-500 mt-3">Try: "Find me a high-protein chicken recipe"</p>
          </div>
        ) : (
          <div className="space-y-4">
            {recipes.map((recipe) => (
              <RecipeCard key={recipe.id} recipe={recipe} canDelete />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default SavedRecipes;
